import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Stack, TextField, Button, Box, Grid, Typography } from '@mui/material';

import { useMessages } from '../../../hooks/useMessages';
import {
    executeSearch,
    searchClear,
} from '../../../store/actions/searchActions';
import { isDateValid, isPhoneNumberValid } from '../../../services/formatUtils';

const isAddressValid = (value) => {
    const err = value.indexOf('*') >= 0 && value.length > 1;
    return !err;
};

const SearchForm = () => {
    const dispatch = useDispatch();
    const { L } = useMessages();
    const search = useSelector((state) => state.search);

    const [params, setParams] = useState({
        firstName: search.params.firstName || '',
        lastName: search.params.lastName || '',
        dateOfBirth: search.params.dateOfBirth || '',
        email: search.params.email || '',
        phoneNumber: search.params.phoneNumber || '',
        address: search.params.address || '',
        city: search.params.city || '',
        postalCode: search.params.postalCode || '',
    });

    const [clear, setClear] = useState(true);
    const [showErrors, setShowErrors] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({
        dateOfBirth: null,
        address: null,
        city: null,
        postalCode: null,
        phoneNumber: null,
    });

    useEffect(() => {
        setClear(
            !(
                params.firstName ||
                params.lastName ||
                params.dateOfBirth ||
                params.email ||
                params.phoneNumber ||
                params.address ||
                params.city ||
                params.postalCode
            )
        );
        validate();
    }, [params]);

    const searchHandler = (e) => {
        e.preventDefault();
        if (valid()) {
            dispatch(executeSearch(params));
            setShowErrors(false);
        } else {
            setShowErrors(true);
        }
    };

    const clearHandler = () => {
        setParams({
            firstName: '',
            lastName: '',
            dateOfBirth: '',
            email: '',
            phoneNumber: '',
            address: '',
            city: '',
            postalCode: '',
        });
        setShowErrors(false);
        dispatch(searchClear());
    };

    const onFieldChange = (event) => {
        const updatedParams = {
            ...params,
        };
        updatedParams[event.target.name] = event.target.value.trimStart();
        setParams(updatedParams);
    };

    const validate = () => {
        setFieldErrors({
            dateOfBirth:
                !params.dateOfBirth || isDateValid(params.dateOfBirth)
                    ? null
                    : L.searchpage_invalid_date,
            address:
                !params.address || isAddressValid(params.address)
                    ? null
                    : L.searchpage_invalid_wildcard,
            city:
                !params.city || isAddressValid(params.city)
                    ? null
                    : L.searchpage_invalid_wildcard,
            postalCode:
                !params.postalCode || isAddressValid(params.postalCode)
                    ? null
                    : L.searchpage_invalid_wildcard,
            phoneNumber:
                !params.phoneNumber || isPhoneNumberValid(params.phoneNumber)
                    ? null
                    : L.searchpage_invalid_phone,
        });
    };

    const valid = () => {
        return (
            !fieldErrors.dateOfBirth &&
            !fieldErrors.address &&
            !fieldErrors.city &&
            !fieldErrors.postalCode &&
            !fieldErrors.phoneNumber
        );
    };

    return (
        <Box component="form" noValidate autoComplete="off">
            <Grid container spacing={2}>
                <Grid item md={6} xs={12}>
                    <Stack spacing={1}>
                        <Typography variant="h5">
                            {L.searchpage_section_person}
                        </Typography>
                        <TextField
                            variant="outlined"
                            type="text"
                            label={L.searchpage_field_first_name}
                            fullWidth
                            size="small"
                            value={params.firstName}
                            name="firstName"
                            onChange={onFieldChange}
                            autoFocus={true}
                        />
                        <TextField
                            variant="outlined"
                            type="text"
                            label={L.searchpage_field_last_name}
                            fullWidth
                            size="small"
                            value={params.lastName}
                            name="lastName"
                            onChange={onFieldChange}
                        />
                        <TextField
                            variant="outlined"
                            type="text"
                            label={L.searchpage_field_dob}
                            fullWidth
                            size="small"
                            value={params.dateOfBirth}
                            name="dateOfBirth"
                            onChange={onFieldChange}
                            error={showErrors && !!fieldErrors.dateOfBirth}
                            helperText={showErrors && fieldErrors.dateOfBirth}
                        />
                        <TextField
                            variant="outlined"
                            type="text"
                            label={L.searchpage_field_email}
                            fullWidth
                            size="small"
                            value={params.email}
                            name="email"
                            onChange={onFieldChange}
                        />
                        <TextField
                            variant="outlined"
                            type="text"
                            label={L.searchpage_field_phone}
                            fullWidth
                            size="small"
                            value={params.phoneNumber}
                            name="phoneNumber"
                            onChange={onFieldChange}
                            error={showErrors && !!fieldErrors.phoneNumber}
                            helperText={showErrors && fieldErrors.phoneNumber}
                        />
                    </Stack>
                </Grid>

                <Grid item md={6} xs={12}>
                    <Stack spacing={1}>
                        <Typography variant="h5">
                            {L.searchpage_section_address}
                        </Typography>

                        <TextField
                            variant="outlined"
                            type="text"
                            label={L.searchpage_field_address}
                            fullWidth
                            size="small"
                            value={params.address}
                            name="address"
                            onChange={onFieldChange}
                            error={showErrors && !!fieldErrors.address}
                            helperText={showErrors && fieldErrors.address}
                        />
                        <TextField
                            variant="outlined"
                            type="text"
                            label={L.searchpage_field_city}
                            fullWidth
                            size="small"
                            value={params.city}
                            name="city"
                            onChange={onFieldChange}
                            error={showErrors && !!fieldErrors.city}
                            helperText={showErrors && fieldErrors.city}
                        />
                        <TextField
                            variant="outlined"
                            type="text"
                            label={L.searchpage_field_postal}
                            fullWidth
                            size="small"
                            value={params.postalCode}
                            name="postalCode"
                            onChange={onFieldChange}
                            error={showErrors && !!fieldErrors.postalCode}
                            helperText={showErrors && fieldErrors.postalCode}
                        />
                    </Stack>
                </Grid>
                <Grid item>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            onClick={searchHandler}
                            disabled={
                                clear ||
                                search.loading ||
                                (showErrors && !valid())
                            }
                            color="primary"
                            type="submit"
                        >
                            {L.searchpage_btn_search}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={clearHandler}
                            disabled={clear || search.loading}
                        >
                            {L.searchpage_btn_clear}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SearchForm;
